import Auth from '@/auth/Auth.js'
import { deepClone } from '@/libs/lodash'
import { filtersData } from '@/variables'
import { addWeeks, addYears, eachWeekOfInterval, format } from 'date-fns'

export const arrayToCSV = function (objArray, seperator = ';', excludeColumns = []) {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray
  const keys = Object.keys(array[0]).filter((key)=> !excludeColumns.includes(key))
  const str = `${keys.map(value => `"${value}"`).join(seperator)}` + '\r\n'

  return array.reduce((str, next) => {
    str += `${keys.map(value => `"${next[value]}"`).join(seperator)}` + '\r\n'
    return str
  }, str)
}

const setWeekDates = ()=> {
  Date.prototype.getWeek = function () {
    const date = new Date(this.getTime())
    date.setHours(0, 0, 0, 0)
    // Thursday in current week decides the year.
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7)
    // January 4 is always in week 1.
    const week1 = new Date(date.getFullYear(), 0, 4)
    // Adjust to Thursday in week 1 and count number of weeks from date to week1.
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000
      - 3 + (week1.getDay() + 6) % 7) / 7)
  }

  // Returns the four-digit year corresponding to the ISO week of the date.
  Date.prototype.getWeekYear = function () {
    const date = new Date(this.getTime())
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7)
    return date.getFullYear()
  }
}
setWeekDates()

export const headersFilters = (value, query, item) => {
  const text = (value || '').toLocaleLowerCase()
  const headerTitle = (item?.raw?.headerTitle || '').toLocaleLowerCase()
  return (text + headerTitle).indexOf(query.toLocaleLowerCase()) > -1
}

export const downloadData = function (sep, tableData, fileName, excludeColumns = []) {
  const d = deepClone(tableData)

  // Get store names and if dutch then replace . with ,
  for (const idx in d) {
    for (const key of Object.keys(d[idx])) {
      if (sep === 'dutch') {
        d[idx][key] = String(d[idx][key])
        d[idx][key] = d[idx][key].replace(',', '')
        d[idx][key] = d[idx][key].replace('.', ',')
      }
    }
  }

  // Replacing done so now set seperator to ;
  if (sep === 'dutch') {
    sep = ';'
  }
  // Convert to csv and download
  const csv = arrayToCSV(d, sep, excludeColumns)
  const data = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
  const link = document.createElement('a')
  link.setAttribute('href', data)
  const today = new Date()
  const dd = today.getDate()
  const mm = today.getMonth() + 1
  const yyyy = today.getFullYear()
  link.setAttribute('download', fileName + '_' + String(dd) + '_' + String(mm) + '_' + String(yyyy) + '.csv')
  link.click()
}

export const optionsFiltersToFormParameters = function (filters = []) {
  const result = {
    collections: [],
    groups: [],
    option_keys_exclude: [],
    options_set_keys_include: [],
    options_set_keys_exclude: [],
  }
  const booleanFilters = ['is_linked', 'stock_active', 'stock_frozen', 'stock_allow_transfers', 'stock_optimize_margin', 'stock_force_minimum', 'stock_exclude_minimum', 'stock_safe_guard', 'stock_infinite_supply', 'stock_exclude_deficit']
  const rangeFilters = ['osp', 'asp', 'stock', 'dc_stock', 'inbounds', 'days_until_inbound', 'skus', 'stores_included']
  const percentageRangeFilters = ['discount', 'sellthrough']
  const singleValueFilters = ['store_inclusions', 'store_exclusions', 'store_not_inclusions', 'store_not_exclusions']
  const datesFilters = ['date_min_days', 'date_max_days']
  const multipleValueFilters = ['option_keys_include', 'option_keys_exclude', 'options_set_keys_include', 'options_set_keys_exclude']

  booleanFilters.forEach(filterName => {
    const filter = filters.find(f => f.type === filterName)

    result[filterName] = filter ? filter.values[0] : null
  })

  rangeFilters.forEach(filterName => {
    const filter = filters.find(f => f.type === filterName)
    if (!filter) {
      result[filterName + '_min'] = null
      result[filterName + '_max'] = null
      return
    }
    const min = parseFloat(filter.values[0])
    const max = parseFloat(filter.values[1])
    result[filterName + '_min'] = isNaN(min) ? null : min
    result[filterName + '_max'] = isNaN(max) ? null : max
  })

  percentageRangeFilters.forEach(filterName => {
    const filter = filters.find(f => f.type === filterName)

    result[filterName + '_min'] = filter ? parseFloat(filter.values[0] / 100) : null
    result[filterName + '_max'] = filter ? parseFloat(filter.values[1] / 100) : null
  })

  singleValueFilters.forEach(filterName => {
    const filter = filters.find(f => f.type === filterName)

    result[filterName] = filter ? filter.values[0] : null
  })

  datesFilters.forEach(filterName => {
    const filter = filters.find(f => f.type === filterName)

    result[filterName + '_before'] = filter ? filter.values[0] : null
    result[filterName + '_after'] = filter ? filter.values[1] : null
  })

  multipleValueFilters.forEach(filterName => {
    const filter = filters.find(f => f.type === filterName)

    if (filter && filter.values) {
      // Convert options_set_key values to integers for backend compatibility
      if (['options_set_keys_include', 'options_set_keys_exclude'].includes(filterName)) {
        result[filterName] = filter.values.map(val => parseInt(val, 10))
      } else {
        result[filterName] = filter.values
      }
    } else {
      result[filterName] = []
    }
  })

  return result
}

export const storesFiltersToFormParameters = function (filters = []) {
  const country = filters.find(f => f.type === 'country')
  const dcKey = filters.find(f => f.type === 'dc_key')
  const proposalKey = filters.find(f => f.type === 'proposal_key')
  const oneStock = filters.find(f => f.type === 'one_stock')
  const storesInclude = filters.find(f => f.type === 'store_keys_include')
  const storesExclude = filters.find(f => f.type === 'store_keys_exclude')

  return {
    country: country ? country.values : [],
    dc_key: dcKey ? dcKey.values : null,
    proposal_key: proposalKey ? proposalKey.values : null,
    one_stock: oneStock ? oneStock.values : null,
    store_keys_include: storesInclude ? storesInclude.values : [],
    store_keys_exclude: storesExclude ? storesExclude.values : [],
  }
}

export const optionSetParametersToFiltersDynamic = function (parameters) {
  const booleanFilters = ['is_linked', 'stock_active', 'stock_frozen', 'stock_allow_transfers', 'stock_optimize_margin', 'stock_force_minimum', 'stock_exclude_minimum', 'stock_safe_guard', 'stock_infinite_supply', 'stock_exclude_deficit']
  const rangeFilters = ['osp', 'asp', 'stock', 'dc_stock', 'inbounds', 'skus', 'stores_included', 'price', 'days_until_inbound', 'original_price']
  const percentageRangeFilters = ['sellthrough', 'discount']
  const singleValueFilters = ['store_inclusions', 'store_exclusions', 'store_not_inclusions', 'store_not_exclusions']
  const datesFilters = ['date_min_days', 'date_max_days']
  const multipleValueFilters = ['option_keys_include', 'option_keys_exclude']
  const filters = []

  booleanFilters.forEach(filterName => {
    if (parameters[filterName] !== null && parameters[filterName] !== undefined) {
      filters.push({
        type: filterName,
        values: getOptionsFilterValuesByType(filterName, [parameters[filterName]]),
        errors: [],
      })
    }
  })

  rangeFilters.forEach(filterName => {
    if (parameters[filterName + '_min'] || parameters[filterName + '_max']) {
      filters.push({
        type: filterName,
        values: getOptionsFilterValuesByType(filterName, [parameters[filterName + '_min'], parameters[filterName + '_max']]),
        errors: [],
      })
    }
  })

  percentageRangeFilters.forEach(filterName => {
    if (parameters[filterName + '_min'] || parameters[filterName + '_max']) {
      filters.push({
        type: filterName,
        values: getOptionsFilterValuesByType(filterName, [parameters[filterName + '_min'], parameters[filterName + '_max']]),
        errors: [],
      })
    }
  })

  singleValueFilters.forEach(filterName => {
    if (parameters[filterName]) {
      filters.push({
        type: filterName,
        values: getOptionsFilterValuesByType(filterName, [parameters[filterName]]),
        errors: [],
      })
    }
  })

  datesFilters.forEach(filterName => {
    if (parameters[filterName + '_before'] || parameters[filterName + '_after']) {
      filters.push({
        type: filterName,
        values: getOptionsFilterValuesByType(filterName, [parameters[filterName + '_before'], parameters[filterName + '_after']]),
        errors: [],
      })
    }
  })

  multipleValueFilters.forEach(filterName => {
    if (parameters[filterName]) {
      filters.push({
        type: filterName,
        values: getOptionsFilterValuesByType(filterName, parameters[filterName]),
        errors: [],
      })
    }
  })

  return filters
}

export const optionSetParametersToFiltersStatic = function (parameters) {
  const filters = []

  if (parameters.option_keys_include.length) {
    filters.push({
      type: 'option_keys_include',
      values: getOptionsFilterValuesByType('stock', parameters.option_keys_include),
      errors: [],
    })
  }

  return filters
}

export const getOptionsFilterValuesByType = function (type, defaultValues = []) {
  let values = defaultValues.slice()

  if (type === 'discount' || type === 'sellthrough') {
    values[0] = defaultValues[0] || 0
    values[1] = defaultValues[1] || 100
  } else if (['dc_stock', 'inbounds', 'days_until_inbound', 'skus'].includes(type)) {
    values[0] = defaultValues[0] || null
    values[1] = defaultValues[1] || null
  } else if (['is_linked', 'stock_active', 'stock_frozen', 'stock_allow_transfers', 'stock_optimize_margin', 'stock_force_minimum', 'stock_exclude_minimum', 'stock_safe_guard', 'stock_infinite_supply', 'stock_exclude_deficit'].includes(type)) {
    values[0] = defaultValues[0]
  } else if (['price', 'original_price', 'asp', 'osp', 'stock', 'skus', 'stores_included'].includes(type)) {
    values[0] = defaultValues[0] || undefined
    values[1] = defaultValues[1] || undefined
  } else if (['date_min_days', 'date_max_days'].includes(type)) {
    values[0] = defaultValues[0] || undefined
    values[1] = defaultValues[1] || undefined
  } else if (type === 'option_keys_include' || type === 'option_keys_exclude') {
    values = [...defaultValues]
  } else if (['options_set_keys_include', 'options_set_keys_exclude'].includes(type)) {
    values = [...defaultValues]
  } else if (['collection_keys', 'group_keys', 'option_keys_exclude', 'store_inclusions', 'store_exclusions', 'store_not_inclusions', 'store_not_exclusions'].includes(type)) {
    values = []
  } else {
    throw new Error('Unknown filter type ' + type)
  }

  return values
}

export const getStoresFilterValuesByType = function (type, defaultValues = []) {
  let values = defaultValues.slice()

  if (['country', 'store_keys_include', 'store_keys_exclude', 'store_type'].includes(type)) {
    values = []
  } else if (['dc_key', 'proposal_key'].includes(type)) {
    values = null
  } else if (['proposal_key', 'one_stock'].includes(type)) {
    values = 'any'
  } else {
    throw new Error('Unknown filter type ' + type)
  }

  return values
}

export const storeSetParametersToFilters = function (parameters) {
  const filters = []
  const singleFilters = ['dc_key', 'proposal_key', 'one_stock']

  singleFilters.forEach(f => {
    if (parameters[f]) {
      filters.push({
        type: f,
        values: parameters[f],
        errors: [],
      })
    }
  })

  if (parameters.country && parameters.country.length) {
    filters.push({
      type: 'country',
      values: parameters.country,
      errors: [],
    })
  }

  if (parameters.store_keys_include && parameters.store_keys_include.length) {
    filters.push({
      type: 'store_keys_include',
      values: parameters.store_keys_include,
      errors: [],
    })
  }

  if (parameters.store_keys_exclude && parameters.store_keys_exclude.length) {
    filters.push({
      type: 'store_keys_exclude',
      values: parameters.store_keys_exclude,
      errors: [],
    })
  }

  return filters
}

export const getStoresSearchDefaultParams = function () {
  return {
    org_key: 0,
    stores_set_key: null,
    set_name: null,
    label: ['All'],
    one_stock: null,
    country: [],
    dc_key: null,
    proposal_key: null,
    properties_include: {},
    properties_exclude: {},
    store_keys_include: [],
    store_keys_exclude: [],
    static_set: false,
  }
}

export const getFilterOptionsByProperties = function (filterName, data, selectedPropertiesValues) {
  const result = []

  for (const item of data) {
    let match = true
    for (const prop of Object.keys(selectedPropertiesValues)) {
      if (selectedPropertiesValues[prop].length === 0) {
        match = true
      } else if (
        (prop !== 'undefined') &&
        item.properties &&
        !selectedPropertiesValues[prop].includes(item.properties[prop])) { // Check if object is not empty (undefined as string) and then check if value is possible given already selected values
        match = false
      }
    }
    if (match === true) {
      result.push(item[filtersData[filterName].keyProp].toString())
    }
  }

  return result
}

export const serializeObjToQuery = function (obj) {
  const str = []
  for (const p in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]))
    }
  }
  return str.join('&')
}

export const getDynamicCapacityErrors = function (capacity) {
  const errors = {}
  if (!capacity.options_set_key) {
    errors.optionSets = 'At least one required'
  }

  if (!capacity.stores_set_key  && !capacity.store_keys?.length ) {
    errors.storeSets = 'At least one required'
  }
  if(!capacity.capacity_type) {
    errors.capacityType = 'Required'
  }

  const mustHave = parseInt(capacity.must_have)
  const niceHave = parseInt(capacity.nice_have)
  const maxHave = parseInt(capacity.max_have)
  const softHave = parseInt(capacity.soft_max_have)

  if (isNaN(mustHave) && isNaN(niceHave) && isNaN(maxHave) && isNaN(softHave)) {
    errors.mustHave = 'At least one required'
    errors.niceToHave = 'At least one required'
    errors.maxHave = 'At least one required'
    errors.softHave = 'At least one required'
  }

  if (
    (!isNaN(mustHave) && !isNaN(niceHave)) &&
    (niceHave < mustHave)
  ) {
    errors.niceToHave = 'Should be >= must have'
  }
  if (
    ((!isNaN(mustHave) || !isNaN(niceHave)) && !isNaN(maxHave)) &&
    ((maxHave < mustHave) || (maxHave < niceHave))
  ) {
    errors.maxHave = 'Should be >= must and nice to have'
  }
  return errors
}

export const getCapacityOptionErrors = function (option) {
  const optionErrors = {}

  if ((!option.option_keys?.length && !option.options_set_key)) {
    optionErrors.options = 'At least one required'
  }
  if (!option.store_keys?.length && !option.stores_set_key) {
    optionErrors.stores = 'At least one required'
  }

  if (!option.capacity_type) {
    optionErrors.capacityType = 'Required'
  }

  const mustHave = parseInt(option.must_have)
  const niceHave = parseInt(option.nice_have)
  const maxHave = parseInt(option.max_have)

  if (isNaN(mustHave) && isNaN(niceHave) && isNaN(maxHave)) {
    optionErrors.mustHave = 'At least one required'
    optionErrors.niceToHave = 'At least one required'
    optionErrors.maxHave = 'At least one required'
  }

  if (
    (!isNaN(mustHave) && !isNaN(niceHave)) &&
    (niceHave < mustHave)
  ) {
    optionErrors.niceToHave = 'Should be >= must have'
  }

  if (
    (!isNaN(mustHave) && !isNaN(niceHave) && !isNaN(maxHave)) &&
    ((maxHave < mustHave) || (maxHave < niceHave))
  ) {
    optionErrors.maxHave = 'Should be >= must and nice to have'
  }

  if (option.capacity_type === 'pieces_per_sku' && option.force && isNaN(mustHave)) {
    optionErrors.mustHave = 'Must have can not be empty'
  }

  return optionErrors
}

export const getDummyStatus = function (dummy, requiredProperties) {
  let hasRequiredProperties = false
  if (dummy.properties) {
    if (!requiredProperties.every(p =>
      (dummy.properties[p]?.force && !dummy.properties[p]) || dummy.properties[p]?.errors)
    ) {
      hasRequiredProperties = true
    }
  }
  return (hasRequiredProperties &&
    dummy.option_name.length &&
    dummy.supplier_key &&
    dummy.sku_names.length &&
    dummy.prices && Object.keys(dummy.prices).length
  ) ? 'completed' : 'in_progress'
}

export const getWeeksSelectorOptions = function (startYear = -1, endYear = 2, onlyFuture = false) {
  const date = new Date()
  return eachWeekOfInterval({
    start: onlyFuture ? date : addYears(date, startYear),
    end: addYears(date, endYear),
  }).map(w => {
    const week = ('0' + w.getWeek()).substr(-2)
    const year = w.getWeekYear()
    return {
      title: `${year} - W${week}`,
      value: `${year}${week}`,
    }
  })
}

export const hexToRgbA = function (hex, opacity) {
  let c
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('')
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]]
    }
    c = '0x' + c.join('')
    return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + opacity + ')'
  } else {
    return hex
  }
}

export const getModuleFromPath = function (path) {
  let currentModule = null
  const modules = ['stock', 'store', 'buy', 'pricing', 'strategy']

  modules.forEach(m => {
    if (path.startsWith('/' + m)) {
      currentModule = m
    }
  })

  return currentModule
}

export const formatValueWithCurrency = function (value, digits, mainCurrency = undefined) {
  if (value === undefined || value === null || value === '') {
    return value
  }

  const currency = mainCurrency || Auth.getUserCurrency().currency || 'EUR'

  // K notation for values >= 100,000
  if (Math.abs(value) >= 1e5) {
    const kValue = value / 1e3
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    return formatter.format(kValue) + 'K'
  }

  const formatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })

  return formatter.format(value)
}

export const formatString = function (value) {
  if (typeof value === 'boolean') {
    value = value ? 'yes' : 'no'
  }
  if (typeof value !== 'string') {
    return value
  }
  let variable = value.replace('_', ' ')
  variable = variable.replace('_', ' ')
  variable = variable.replace('_', ' ')
  variable = variable.replace('_', ' ')

  return variable.charAt(0).toUpperCase() + variable.slice(1)
}

export const groupAnalyticsProperties = function (analyticsProperties) {
  const array = []
  const headers = {}
  const propertiesSorted = analyticsProperties.slice().sort()

  for (const key of propertiesSorted) {
    const title = key.split('/')
    if (headers[title[1]] === undefined) {
      headers[title[1]] = { type: 'subheader', title: formatString(title[1] || '') }
      array.push(headers[title[1]])
    }
    array.push({
      value: key,
      headerTitle: formatString(title[1] || ''),
      title: formatString(title[2] || '')
    })
  }
  if (!array.length) {
    return []
  }
  return array
}

// Converts an integer week offset from "now" (0 = current week, negative = past,
// positive = future) into the ISO `YYYYWW` numeric week format used for
// week_min/week_max. Uses ISO week-numbering year (RRRR) + ISO week (II), not
// calendar year + local week, so it stays correct across year boundaries
// (matches the ISO 8601 convention StrategyManageSeasons.vue's formatDateToWeek uses).
export const weekOffsetToWeek = function (offsetWeeks, from = new Date()) {
  return Number(format(addWeeks(from, offsetWeeks), 'RRRRII'))
}

export const formatLargeNumber = function (value, precision) {
  if (value === undefined || value === null || value === '') {
    return '0'
  }
  if (value === 0) {
    return '0'
  }
  if (Math.abs(value) < 1) {
    return Number(value).toFixed(precision === undefined ? 2 : precision)
  }
  const formatter = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: precision || 0,
    maximumFractionDigits: precision || 1,
  })
  // K notation for values >= 100,000
  if (Math.abs(value) >= 1e5) {
    const kFormatter = new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    return kFormatter.format(value / 1e3) + 'K'
  }
  return formatter.format(value)
}

export const getAvailabilityColorClass = function (value) {
  if (value <= 100 && value > 80) {
    return 'text-green'
  }

  if (value < 80 && value >= 50) {
    return 'text-orange'
  }

  if (value < 50 && value >= 0) {
    return 'text-red'
  }
}

export const getTransfersOverviewData = function (transfersDataSend, transfersDataReceive) {
  const total = {
    stock: transfersDataSend.total.stock,
    receive: 0,
    pieces: transfersDataSend.total.pieces,
    goal: 0
  }
  const visitedStores = new Set()
  const parentTable = transfersDataSend.parent_table.map(rowSend => {
    const toStore = transfersDataReceive.parent_table.find(rowReceive => rowSend.store_key === rowReceive.to_store_key)
    visitedStores.add(rowSend.store_key)
    const receive = toStore ? toStore.pieces : 0
    const goal = rowSend.stock - rowSend.pieces + (toStore ? toStore.pieces : 0)

    total.receive += receive
    total.goal += goal

    return {
      store_key: rowSend.store_key,
      stock: rowSend.stock,
      receive: receive,
      pieces: rowSend.pieces,
      goal: goal,
    }
  })
  const storesNotVisited = transfersDataReceive.parent_table.filter(row => !visitedStores.has(row.to_store_key))
    .map(row => {
      total.receive += row.pieces
      total.goal += row.pieces
      return {
        store_key: row.to_store_key,
        stock: 0,
        receive: row.pieces,
        pieces: 0,
        goal: row.pieces,
      }
    })

  return {
    parent_table: [...parentTable, ...storesNotVisited],
    total,
  }
}

export const getStatusColorClass = function (product, suffix = '--text', prop = 'status') {
  // Vuetify 4 renamed color text utilities from `<color>--text` to `text-<color>`.
  // Callers passing the legacy `--text` suffix get the new class; any other suffix is
  // appended verbatim for backward compat.
  const build = (color) => suffix === '--text' ? `text-${color}` : `${color}${suffix}`
  if (['generated', 'to_order'].includes(product[prop])) {
    return build('yellow')
  }

  if (product[prop] === 'collection') {
    return build('orange')
  }

  if (['planned', 'in_progress', 'stock'].includes(product[prop])) {
    return build('primary')
  }

  if (product[prop] === 'ordered' || product[prop] === 'shopping') {
    return build('green')
  }

  if (product[prop] === 'delivered') {
    return build('purple')
  }
  if (product[prop] === 'out_of_stock') {
    return 'red' + suffix
  }
}

export function getWordAt (str, index) {
  // 1. Find the start of the word (first space before index)
  let start = str.lastIndexOf(' ', index)
  start = start === -1 ? 0 : start + 1 // Start at 0 if no space found

  // 2. Find the end of the word (first space after index)
  let end = str.indexOf(' ', index)
  end = end === -1 ? str.length : end // End at string length if no space found

  // 3. Extract the word
  return str.substring(start, end)
}

export const formatAodToDo = (value) => {
  const tableData = []
  const keys = value.columns
  const dataCount = value.rows.length
  for(let i = 0; i < dataCount; i++) {
    const row = {}
    for(let j = 0; j < keys.length; j++){
      row[keys[j]] = value.rows[i][j]
    }
    tableData.push(row)
  }
  return tableData
}
